import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  formatDateOnly,
  fromCents,
  statusLabel,
  statusTone,
  toCents,
  errorWords,
} from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { FactStrip } from "@/components/app/patterns/fact-strip";
import { Fact } from "@/components/app/patterns/fact";
import { RecordHeader } from "@/components/app/patterns/record-header";
import { daysPastDue, owes } from "@/components/app/payments/money";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InvoiceAdminActions } from "./-components/invoice-admin-actions";
import { InvoiceOnlinePayment } from "./-components/invoice-online-payment";
import { InvoiceLinesTable } from "./-components/invoice-detail-tables";
import { InvoicePaymentsTable } from "./-components/invoice-payments-table";
import { InvoicePaymentLinks } from "./-components/invoice-payment-links";
import { RecordPaymentDialog } from "./-components/record-payment-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { InvoicePaidOverlay } from "@/components/app/celebration/invoice-paid-overlay";
import { billingPeriodLabel } from "@/lib/facility/billing-period";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { orpc, type Output, orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { BilledResidentAvatar } from "../-components/billed-resident-avatar";
import { BilledResidentLink } from "@/routes/_authenticated/_financials/-components/billed-resident-link";
import { moneyReaders } from "@/lib/query/query-readers";

export const Route = createFileRoute("/_authenticated/_financials/invoice-roll/$invoiceId")({
  component: InvoiceDetail,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One invoice: its lines, what has been paid against it, and what is left. */
function InvoiceDetail() {
  const { invoiceId } = Route.useParams();
  const queryClient = useQueryClient();
  const [taking, setTaking] = useState(false);
  const [reversing, setReversing] = useState<string | null>(null);
  const [paid, setPaid] = useState<Output<typeof orpcClient.financials.invoices.get> | null>(null);

  const invoice = useQuery(orpc.financials.invoices.get.queryOptions({ input: { invoiceId } }));
  /* Asked of the invoice's own building, as the API asks it. Taking a card is
     financial:reconcile there, not the ordinary financial write. */
  const canWrite = useCan("financial:manage", invoice.data?.facilityId);
  const canTakeCard = useCan("financial:reconcile", invoice.data?.facilityId);
  const payments = useQuery(
    orpc.financials.invoices.payments.queryOptions({ input: { invoiceId } }),
  );
  const residentId = invoice.data?.residentId ?? "";
  const resident = useQuery({
    ...orpc.care.residents.get.queryOptions({ input: { residentId } }),
    enabled: Boolean(residentId),
  });

  const refresh = async () => {
    await invalidateKeys(queryClient, moneyReaders());
  };

  const clear = useMutation(
    orpc.financials.payments.clear.mutationOptions({
      onSuccess: async () => {
        toast.success("Payment cleared");
        await refresh();
        const settled = await queryClient.fetchQuery(
          orpc.financials.invoices.get.queryOptions({ input: { invoiceId } }),
        );
        if (settled.status === "paid") setPaid(settled);
      },
      onError: fail,
    }),
  );
  const failPayment = useMutation(
    orpc.financials.payments.fail.mutationOptions({
      onSuccess: async () => {
        toast.success("Payment reversed");
        await refresh();
      },
      onError: fail,
    }),
  );

  if (invoice.isPending) return <Skeleton className="h-64 w-full" />;
  // A blank page after a failed read reads as an invoice with nothing on it.
  if (!invoice.data)
    return (
      <>
        <PageHeader
          title="Invoice"
          meta={
            <Link
              to="/invoice-roll"
              search={{ view: "invoices", status: undefined, q: undefined }}
              className="hover:underline"
            >
              Back to invoices
            </Link>
          }
        />
        <ReadFailed
          message="This invoice could not be loaded."
          error={invoice.error}
          onRetry={() => void invoice.refetch()}
          retrying={invoice.isFetching}
        />
      </>
    );

  const owed = canWrite && invoice.data.status !== "paid" && invoice.data.status !== "void";
  // In cents: a zero discount arrives as "$0", and comparing with "$0.00"
  // offered to remove a discount that was never there.
  const hasDiscount = toCents(invoice.data.discountAmount ?? "0") > 0;
  const aging =
    invoice.data.status === "void" || !owes(invoice.data.balance)
      ? 0
      : daysPastDue(invoice.data.dueDate);

  return (
    <>
      {paid ? <InvoicePaidOverlay invoice={paid} onClose={() => setPaid(null)} /> : null}
      <RecordHeader
        back={{
          label: "Invoices",
          link: {
            to: "/invoice-roll",
            search: { view: "invoices", status: undefined, q: undefined },
          },
        }}
        media={resident.data ? <BilledResidentAvatar resident={resident.data} /> : null}
        title={invoice.data.invoiceNumber ?? "Invoice"}
        titleId="invoice-title"
        badges={
          <Badge variant={statusTone(invoice.data.status)}>
            {statusLabel(invoice.data.status)}
          </Badge>
        }
        // The state in words, so "overdue" never rests on a colour.
        facts={[
          resident.data ? (
            <BilledResidentLink key="resident" residentId={residentId} resident={resident.data} />
          ) : null,
          billingPeriodLabel(invoice.data),
          `Due ${formatDateOnly(invoice.data.dueDate)}`,
          aging > 0 ? (
            <span key="aging" className="text-crit-ink font-medium">
              {aging} {aging === 1 ? "day" : "days"} past due
            </span>
          ) : null,
        ]}
        actions={
          <>
            {owed ? (
              <>
                {canTakeCard ? (
                  <InvoiceOnlinePayment
                    invoiceId={invoiceId}
                    residentId={residentId}
                    invoiceNumber={invoice.data.invoiceNumber}
                    balance={invoice.data.balance}
                  />
                ) : null}
                <Button onClick={() => setTaking(true)}>Record a payment</Button>
              </>
            ) : null}
            <InvoiceAdminActions
              invoice={invoice.data}
              canWrite={canWrite}
              adjust={owed ? { hasDiscount } : null}
            />
          </>
        }
        strip={
          <FactStrip>
            <Fact label="Total" value={invoice.data.totalDue} />
            {/* The API writes a zero discount as "$0"; every other figure says "$0.00". */}
            <Fact label="Discount" value={fromCents(toCents(invoice.data.discountAmount ?? "0"))} />
            <Fact label="Paid" value={invoice.data.amountPaid ?? "$0.00"} />
            <Fact
              label="Balance"
              value={
                <span className={aging > 0 ? "text-crit-ink" : undefined}>
                  {invoice.data.balance}
                </span>
              }
              sub={aging > 0 ? "Past due" : undefined}
            />
          </FactStrip>
        }
      />

      <InvoiceLinesTable lines={invoice.data.lineItems} />

      <InvoicePaymentsTable
        payments={payments}
        canWrite={canWrite}
        facilityId={invoice.data.facilityId ?? null}
        onClear={(paymentId) => clear.mutate({ paymentId })}
        onReverse={setReversing}
      />
      <InvoicePaymentLinks invoiceId={invoiceId} canWrite={canWrite} />

      <RecordPaymentDialog
        invoiceId={invoiceId}
        facilityId={invoice.data.facilityId}
        balance={invoice.data.balance}
        open={taking}
        onOpenChange={setTaking}
      />
      {reversing ? (
        <RemoveConfirm
          title="Reverse this payment?"
          description="It is marked as failed, as money that never arrived, and cannot be cleared again. A payment that did arrive and went back is a refund instead."
          confirmLabel="Reverse payment"
          onConfirm={() => failPayment.mutate({ paymentId: reversing })}
          onClose={() => setReversing(null)}
        />
      ) : null}
    </>
  );
}
